import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-breadcrumb>` says about itself.
 *
 * Two things here are worth knowing before reading the sections.
 *
 * The trail can be given TWO ways and they are not the same mechanism. The `items` property is the
 * one a screen normally uses. Failing that, the element reads its own `<a href>` and `<span>`
 * children ONCE, on connection, and turns them into the same list — it does not project them, and
 * there is no `<slot>` anywhere in its shadow root, so those children are a DATA SOURCE that is
 * then re-drawn rather than content that is displayed. That is why `slots` below is `[]` and why
 * both forms get an example: a reader who assumes the children are slotted will wonder why editing
 * one later changes nothing.
 *
 * This is the one piece in the Navigation group that is NOT part of the workspace frame. It is
 * placed inside a page, at the head of it, by whatever draws that page — so its placements are the
 * page kinds rather than `seam`. Where the source did not settle it: a guided-flow step and an
 * app's home page are both surfaces with nothing above them to climb back to, so neither is
 * claimed here.
 */
const docs: ComponentDocs = {
  aliases: ['breadcrumbs', 'crumb trail', 'ancestor trail', 'path bar', 'you-are-here trail'],

  answers: [
    'show where this page sits under its parent',
    'give somebody a way back to the list they came from',
    'show the ancestor chain of a deep configuration screen',
    'name the current page at the end of its own trail',
  ],

  placements: ['recordPage', 'appPage'],

  inputs: [
    {
      name: 'items',
      // No `attribute`: the trail is a list of objects and there is no honest way to put one in an
      // attribute. Set it as a property.
      type: 'json',
      description:
        'The trail, root first, as `{ label, href? }`. The LAST entry is drawn as the current page ' +
        'rather than as a link, whether or not it carries an href. Setting this replaces the trail.',
    },
  ],

  // Read out of the source rather than assumed: this element dispatches no CustomEvent of any kind.
  // A crumb with an href is a real `<a>`, so following it is the browser's ordinary navigation and
  // nothing has to be wired up for it to work.
  events: [],

  // No `<slot>` is rendered. Light-DOM `<a>`/`<span>` children are READ as the trail (see the
  // header) and then re-drawn inside the shadow root — they are never projected.
  state: [],

  slots: [],

  tokens: [
    { token: '--caos-font', controls: 'The typeface the crumbs are set in.' },
    { token: '--caos-color-text-muted', controls: 'The colour of an ancestor crumb and of the chevron between crumbs.' },
    { token: '--caos-color-text', controls: 'The colour of the last crumb, the current page.' },
    { token: '--caos-color-accent', controls: 'The colour a linked crumb takes on hover.' },
    { token: '--caos-radius-sm', controls: 'The corner radius of the focus ring around a linked crumb.' },
    { token: '--caos-focus-ring', controls: 'The ring drawn on a linked crumb reached by keyboard.' },
    { token: '--caos-space-1', controls: 'The gap between a crumb and the chevron after it.' },
  ],

  parts: [
    { part: 'nav', is: 'the landmark wrapping the trail' },
    { part: 'list', is: 'the ordered list of crumbs' },
    { part: 'item', is: 'one crumb and its separator' },
    { part: 'link', is: 'a crumb that navigates' },
    { part: 'current', is: 'the last crumb, the one you are on' },
    { part: 'separator', is: 'the chevron between two crumbs' },
  ],

  methods: [],

  placing:
    'It goes at the very head of a page, above the page heading, in the first region of the layout. ' +
    'It needs nothing around it and carries no record binding of its own — the page that knows where ' +
    'it sits builds the trail and hands it over. A crumb is capped at 22 characters and ellipsised, so ' +
    'a long record name does not push the trail across the whole width.',

  whenToUse:
    'When a page has a parent worth going back to and the way back is not otherwise obvious — a record ' +
    'under its list, a configuration screen several levels inside Setup.',

  insteadUse: [
    { name: 'WorkspaceTabs', when: 'You want to move between an app’s destinations rather than climb back up from one of them.' },
    { name: 'TabStrip', when: 'The choices are facets of the record you are already on, not ancestors of it.' },
    { name: 'PageHeader', when: 'You want to name the surface and say how many things are on it, not show the trail to it.' },
  ],

  accessibility:
    'The trail is a real `<nav aria-label="Breadcrumb">` holding an ordered list, so it is announced as ' +
    'a navigation landmark and its crumbs are announced in order and counted. The last crumb is marked ' +
    '`aria-current="page"` and is deliberately not a link, and the chevrons between crumbs are hidden ' +
    'from assistive technology. Linked crumbs are ordinary anchors, so keyboard reach and the focus ring ' +
    'are already handled. What is left to the author is the LABELS: a crumb reading "Detail" tells ' +
    'somebody nothing about what it goes back to.',

  examples: [
    {
      key: 'trail',
      label: 'A record under its list',
      summary: 'The ordinary form: the trail handed over as data, with the last entry drawn as the current page.',
      placement: {
        component: 'caos-breadcrumb',
        inputs: {
          items: [
            { label: 'Sales Atlas', href: '/sales' },
            { label: 'Orders', href: '/sales/orders' },
            { label: 'ORD-4471' },
          ],
        },
      },
    },
    {
      key: 'declarative',
      label: 'Written as markup',
      summary:
        'The same trail written as `<a>` and `<span>` children instead of as data — read once on ' +
        'connection and re-drawn, which is what a page with a fixed trail can write by hand.',
      placement: {
        component: 'caos-breadcrumb',
        children: [
          { component: 'a', inputs: { href: '/setup' }, children: ['Setup'] },
          { component: 'a', inputs: { href: '/setup/objects' }, children: ['Object Manager'] },
          { component: 'span', children: ['Order'] },
        ],
      },
    },
  ],
};

export default docs;
